/**
 * Browser error reporting to the API's log pipeline (`POST /api/v5/client-events`).
 * Deliberately homegrown and vendor-free: the browser holds no telemetry keys
 * and the strict CSP stays closed. The reporter is fire-and-forget — it
 * ignores every response, never throws, and can never report itself (its own
 * fetch failures are swallowed, and the API excludes the endpoint from access
 * logs and traces).
 */

type ClientErrorKind = "error" | "unhandledrejection" | "react-boundary";

export type ClientErrorReport = {
  kind: ClientErrorKind;
  message: unknown;
  stack?: string | undefined;
  componentStack?: string | undefined;
  relatedRequestId?: string | undefined;
};

const DEDUPE_WINDOW_MS = 30_000;
const MAX_REPORTS_PER_PAGE = 10;
const RECENT_CAP = 50;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let sent = 0;
const recent = new Map<string, number>();

/** Test hook: reset the dedupe window and page cap. */
export function resetClientErrorReporting(): void {
  sent = 0;
  recent.clear();
}

/**
 * Cardinality- and privacy-safe route identity: id-shaped path segments are
 * templated away and the result is capped, so reports carry the page shape,
 * never document ids, tokens, or query strings.
 */
export function sanitizeRoute(pathname: string): string {
  const template = pathname
    .split("/")
    .map((segment) =>
      /^[0-9a-fA-F-]{8,}$/.test(segment) || /^\d+$/.test(segment) || segment.length > 32
        ? ":id"
        : segment,
    )
    .join("/");
  return (template || "/").slice(0, 500);
}

export function reportClientError(input: ClientErrorReport): void {
  try {
    if (sent >= MAX_REPORTS_PER_PAGE) return;
    const message = String(input.message ?? "unknown").slice(0, 2000);
    const now = Date.now();
    const key = `${input.kind}:${message}:${(input.stack ?? "").slice(0, 400)}`;
    const last = recent.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
    recent.set(key, now);
    if (recent.size > RECENT_CAP) {
      const oldest = recent.keys().next().value;
      if (oldest !== undefined) recent.delete(oldest);
    }
    sent += 1;
    const relatedRequestId =
      typeof input.relatedRequestId === "string" && UUID.test(input.relatedRequestId)
        ? input.relatedRequestId.toLowerCase()
        : undefined;
    const body = JSON.stringify({
      eventId: crypto.randomUUID(),
      kind: input.kind,
      message,
      ...(input.stack ? { stack: String(input.stack).slice(0, 8000) } : {}),
      ...(input.componentStack
        ? { componentStack: String(input.componentStack).slice(0, 8000) }
        : {}),
      route: sanitizeRoute(window.location.pathname),
      buildId: __APP_BUILD_ID__,
      ...(relatedRequestId ? { relatedRequestId } : {}),
    });
    void fetch("/api/v5/client-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    // The reporter must never become an error source of its own.
  }
}

/** Install BEFORE createRoot so render-time failures are already covered. */
export function installGlobalErrorReporting(): void {
  window.addEventListener("error", (event) => {
    reportClientError({
      kind: "error",
      message: event.message || "unknown error",
      stack: event.error instanceof Error ? event.error.stack : undefined,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = event.reason;
    reportClientError({
      kind: "unhandledrejection",
      message: reason instanceof Error ? reason.message : String(reason ?? "unknown"),
      stack: reason instanceof Error ? (reason.stack ?? undefined) : undefined,
      relatedRequestId: (reason as { traceId?: string } | null)?.traceId,
    });
  });
}
